import { useEffect, useState } from "react";
import type {
  YearlyCollection,
  DonatedBeneficiary,
} from "./revenueActivity.types";
import {
  getCollaborators,
  getDonatedBeneficiaries,
} from "../revenueActivity.api";
import "./RevenueHistoryModal.css";

interface RevenueHistoryModalProps {
  currentYear: number;
  yearlyCollections: YearlyCollection[];
  onClose: () => void;
}

const euroFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
});

const dateFormatter = new Intl.DateTimeFormat("es-ES", {
  day: "2-digit",
  month: "long",
  year: "numeric",
});

function formatEuro(value: number | null): string {
  if (value === null) {
    return "Pendiente de regularización";
  }

  return euroFormatter.format(value);
}

function formatDate(value: string): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return dateFormatter.format(date);
}

function RevenueHistoryModal({
  currentYear,
  yearlyCollections,
  onClose,
}: RevenueHistoryModalProps) {
  const [showCollaborators, setShowCollaborators] = useState(false);
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);
  const [collaboratorsError, setCollaboratorsError] = useState("");

  const [showDonated, setShowDonated] = useState(false);
  const [donatedBeneficiaries, setDonatedBeneficiaries] = useState<
    DonatedBeneficiary[]
  >([]);
  const [loadingDonated, setLoadingDonated] = useState(false);
  const [donatedError, setDonatedError] = useState("");

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (showDonated) {
          setShowDonated(false);
        } else if (showCollaborators) {
          setShowCollaborators(false);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, showCollaborators, showDonated]);

  const collectionsByYear = [...yearlyCollections].sort(
    (a, b) => b.year - a.year,
  );

  if (!collectionsByYear.some((item) => item.year === currentYear)) {
    collectionsByYear.unshift({
      year: currentYear,
      totalCollected: 0,
      ivaDescontado: null,
      ingresosSinIVA: null,
      otrosImpuestosHacienda: null,
      gastosEmpresa: null,
      beneficioNeto: null,
      objetivoDonacion: null,
      totalDonations: 0,
      pendienteDonar: null,
    });
  }

  const openCollaborators = async () => {
    setShowCollaborators(true);
    setCollaboratorsError("");
    setLoadingCollaborators(true);

    try {
      const data = await getCollaborators();
      setCollaborators(data.collaborators.map((item) => item.nombre));
    } catch (error) {
      setCollaboratorsError(
        error instanceof Error
          ? error.message
          : "No se han podido cargar los colaboradores.",
      );
    } finally {
      setLoadingCollaborators(false);
    }
  };

  const openDonated = async () => {
    setShowDonated(true);
    setDonatedError("");
    setLoadingDonated(true);

    try {
      const data = await getDonatedBeneficiaries();
      setDonatedBeneficiaries(data.ayudas);
    } catch (error) {
      setDonatedError(
        error instanceof Error
          ? error.message
          : "No se han podido cargar las donaciones realizadas.",
      );
    } finally {
      setLoadingDonated(false);
    }
  };

  return (
    <div
      className="revenue-modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="revenue-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="revenue-modal-title"
      >
        <header className="revenue-modal__header">
          <div>
            <span className="revenue-modal__eyebrow">LINKServs</span>
            <h2 id="revenue-modal-title">Histórico económico</h2>
            <p>Ingresos, gastos y destino social por año.</p>
          </div>

          <button
            type="button"
            className="revenue-modal__close"
            onClick={onClose}
            aria-label="Cerrar ventana"
          >
            ×
          </button>
        </header>

        <div className="revenue-modal__content">
          {collectionsByYear.length > 0 ? (
            <div className="revenue-modal__list">
              {collectionsByYear.map((item) => (
                <article
                  key={item.year}
                  className={`revenue-modal__year ${
                    item.year === currentYear
                      ? "revenue-modal__year--current"
                      : ""
                  }`}
                >
                  <div className="revenue-modal__year-info">
                    <span className="revenue-modal__year-number">
                      {item.year}
                    </span>

                    {item.year === currentYear && (
                      <span className="revenue-modal__current-label">
                        Año actual
                      </span>
                    )}
                  </div>

                  <div className="revenue-modal__year-breakdown">
                    <div>
                      <span>Ingresos brutos de suscripciones</span>
                      <strong>{formatEuro(item.totalCollected)}</strong>
                    </div>

                    <div>
                      <span>IVA regularizado</span>
                      <strong>{formatEuro(item.ivaDescontado)}</strong>
                    </div>

                    <div>
                      <span>Ingresos sin IVA</span>
                      <strong>{formatEuro(item.ingresosSinIVA)}</strong>
                    </div>

                    <div>
                      <span>Otros impuestos de Hacienda</span>
                      <strong>{formatEuro(item.otrosImpuestosHacienda)}</strong>
                    </div>

                    <div>
                      <span>Gastos de empresa</span>
                      <strong>{formatEuro(item.gastosEmpresa)}</strong>
                    </div>

                    <div className="revenue-modal__year-total">
                      <span>Beneficio neto</span>
                      <strong>{formatEuro(item.beneficioNeto)}</strong>
                    </div>

                    <div>
                      <span>Objetivo de donación (50 %)</span>
                      <strong>{formatEuro(item.objetivoDonacion)}</strong>
                    </div>

                    <div>
                      <span>Donaciones realizadas</span>
                      <strong>{formatEuro(item.totalDonations)}</strong>
                    </div>

                    <div className="revenue-modal__year-total">
                      <span>Pendiente de donar</span>
                      <strong>{formatEuro(item.pendienteDonar)}</strong>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className="revenue-modal__empty">
              Todavía no hay información económica registrada.
            </p>
          )}
        </div>

        <footer className="revenue-modal__footer flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          {/* Texto a la izquierda */}
          <span className="min-w-0 flex-1 text-left">
            <strong>
              El 50 % de los beneficios netos obtenidos se destina directamente
              a acciones solidarias y donaciones. Otro 30 % se reserva para
              la creación y desarrollo de una fundación sin ánimo de lucro, que
              también dedicará sus recursos a impulsar estas mismas iniciativas
              sociales y ayudar a quienes más lo necesitan.El otro 20 % al 
              desarrollo y crecimiento de la empresa.
            </strong>
          </span>

          {/* Botones a la derecha */}
          <div className="revenue-modal__actions flex w-full flex-col gap-3 lg:w-64 lg:shrink-0">
            <button
              type="button"
              className="revenue-modal__accept flex min-h-10 w-full items-center justify-center text-center"
              onClick={openCollaborators}
            >
              Ver profesionales colaboradores
            </button>

            <button
              type="button"
              className="revenue-modal__accept flex min-h-10 w-full items-center justify-center text-center"
              onClick={openDonated}
            >
              Ver beneficiarios de las donaciones
            </button>

            <button
              type="button"
              className="revenue-modal__accept flex min-h-10 w-full items-center justify-center text-center"
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>
        </footer>
      </section>

      {/* Modal de profesionales colaboradores */}
      {showCollaborators && (
        <div
          className="revenue-modal-overlay revenue-modal-overlay--nested"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowCollaborators(false);
            }
          }}
        >
          <section
            className="revenue-modal revenue-modal--collaborators"
            role="dialog"
            aria-modal="true"
            aria-labelledby="collaborators-modal-title"
          >
            <header className="revenue-modal__header">
              <div>
                <span className="revenue-modal__eyebrow">LINKServs</span>
                <h2 id="collaborators-modal-title">
                  Profesionales colaboradores
                </h2>
                <p>Profesionales con al menos un pago de suscripción válido.</p>
              </div>

              <button
                type="button"
                className="revenue-modal__close"
                onClick={() => setShowCollaborators(false)}
                aria-label="Cerrar colaboradores"
              >
                ×
              </button>
            </header>

            <div className="revenue-modal__content">
              {loadingCollaborators ? (
                <p className="revenue-modal__empty">
                  Cargando profesionales colaboradores...
                </p>
              ) : collaboratorsError ? (
                <p className="revenue-modal__empty" role="alert">
                  {collaboratorsError}
                </p>
              ) : collaborators.length > 0 ? (
                <>
                  <p>
                    Total de profesionales:{" "}
                    <strong>{collaborators.length}</strong>
                  </p>

                  <div className="revenue-modal__list">
                    {collaborators.map((nombre, index) => (
                      <div
                        className="revenue-modal__year"
                        key={`${nombre}-${index}`}
                      >
                        <strong>{nombre}</strong>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="revenue-modal__empty">
                  Todavía no hay profesionales colaboradores registrados.
                </p>
              )}
            </div>

            <footer className="revenue-modal__footer">
              <span>
                La lista se genera automáticamente a partir de los pagos
                válidos.
              </span>

              <button
                type="button"
                className="revenue-modal__accept"
                onClick={() => setShowCollaborators(false)}
              >
                Volver al histórico
              </button>
            </footer>
          </section>
        </div>
      )}

      {/* Modal de beneficiarios de las donaciones */}
      {showDonated && (
        <div
          className="revenue-modal-overlay revenue-modal-overlay--nested"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowDonated(false);
            }
          }}
        >
          <section
            className="revenue-modal revenue-modal--collaborators"
            role="dialog"
            aria-modal="true"
            aria-labelledby="donated-modal-title"
          >
            <header className="revenue-modal__header">
              <div>
                <span className="revenue-modal__eyebrow">LINKServs</span>
                <h2 id="donated-modal-title">
                  Beneficiarios de las donaciones
                </h2>
                <p>Ayudas realizadas y autorizadas para su publicación.</p>
              </div>

              <button
                type="button"
                className="revenue-modal__close"
                onClick={() => setShowDonated(false)}
                aria-label="Cerrar beneficiarios"
              >
                ×
              </button>
            </header>

            <div className="revenue-modal__content">
              {loadingDonated ? (
                <p className="revenue-modal__empty">
                  Cargando donaciones realizadas...
                </p>
              ) : donatedError ? (
                <p className="revenue-modal__empty" role="alert">
                  {donatedError}
                </p>
              ) : donatedBeneficiaries.length > 0 ? (
                <>
                  <p>
                    Total de donaciones publicadas:{" "}
                    <strong>{donatedBeneficiaries.length}</strong>
                  </p>

                  <div className="revenue-modal__list">
                    {donatedBeneficiaries.map((item) => (
                      <article
                        className="revenue-modal__year"
                        key={item.donacion_id}
                      >
                        <div className="revenue-modal__year-info">
                          <strong>{item.nombre}</strong>
                          <span className="revenue-modal__current-label">
                            {item.tipo}
                          </span>
                        </div>

                        <div className="revenue-modal__year-breakdown">
                          {item.descripcion_publica && (
                            <div>
                              <span>Sobre esta ayuda</span>
                              <strong>{item.descripcion_publica}</strong>
                            </div>
                          )}

                          {item.concepto_publico && (
                            <div>
                              <span>Destino de la donación</span>
                              <strong>{item.concepto_publico}</strong>
                            </div>
                          )}

                          {item.localidad_publica && (
                            <div>
                              <span>Localidad</span>
                              <strong>{item.localidad_publica}</strong>
                            </div>
                          )}

                          <div>
                            <span>Fecha de la donación</span>
                            <strong>{formatDate(item.fecha)}</strong>
                          </div>

                          <div className="revenue-modal__year-total">
                            <span>Importe donado</span>
                            <strong>{formatEuro(item.importe)}</strong>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </>
              ) : (
                <p className="revenue-modal__empty">
                  Todavía no hay donaciones autorizadas para aparecer en el
                  histórico público.
                </p>
              )}
            </div>

            <footer className="revenue-modal__footer">
              <span>
                Solo se muestran los datos públicos autorizados por cada
                beneficiario.
              </span>

              <button
                type="button"
                className="revenue-modal__accept"
                onClick={() => setShowDonated(false)}
              >
                Volver al histórico
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}

export default RevenueHistoryModal;
